package dairy.mynotes.noteslist

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.viewmodel.compose.viewModel
import dairy.mynotes.navigation.Navigator
import dairy.mynotes.repository.Note
import dairy.mynotes.reusablecomponents.AddIcon
import dairy.mynotes.reusablecomponents.AppTopBar
import dairy.mynotes.reusablecomponents.DeleteIcon
import dairy.mynotes.reusablecomponents.EditIcon
import dairy.mynotes.ui.theme.AppColors
import dairy.mynotes.utils.formatDate

// Accent colors cycling per category
private val categoryAccents = listOf(
    AppColors.AccentBlue,
    AppColors.AccentGreen,
    AppColors.AccentPurple,
    AppColors.AccentOrange,
    AppColors.AccentRed,
    AppColors.Secondary,
)

@Composable
fun NotesListScreen(
    navigator: Navigator? = null,
    viewModel: NotesListViewModel = viewModel { NotesListViewModel() }
) {
    val uiState by viewModel.uiState.collectAsState()
    // State for delete confirmation dialogs
    var noteToDelete by remember { mutableStateOf<Note?>(null) }
    var categoryToDelete by remember { mutableStateOf<NoteSection?>(null) }

    // Load notes when screen first appears
    LaunchedEffect(Unit) {
        viewModel.loadNotes()
    }

    Scaffold(
        containerColor = AppColors.Background,
        topBar = {
            AppTopBar(title = "My Notes", navigator = navigator)
        },
        floatingActionButton = {
            FloatingActionButton(
                onClick = { navigator?.navigateToAddNote() },
                containerColor = AppColors.Primary,
                contentColor = AppColors.OnPrimary,
                shape = CircleShape
            ) {
                AddIcon(contentDescription = "Add Note")
            }
        }
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
        ) {
            when {
                uiState.isLoading -> {
                    CircularProgressIndicator(
                        color = AppColors.Primary,
                        modifier = Modifier.align(Alignment.Center)
                    )
                }
                uiState.error != null -> {
                    Text(
                        text = uiState.error ?: "",
                        color = AppColors.Error,
                        modifier = Modifier.align(Alignment.Center).padding(16.dp)
                    )
                }
                uiState.sections.isEmpty() -> {
                    Column(
                        modifier = Modifier.align(Alignment.Center),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Text(
                            text = "📝",
                            fontSize = 48.sp
                        )
                        Spacer(modifier = Modifier.height(12.dp))
                        Text(
                            text = "No notes yet",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.SemiBold,
                            color = AppColors.TextPrimary
                        )
                        Spacer(modifier = Modifier.height(4.dp))
                        Text(
                            text = "Tap + to add your first note",
                            style = MaterialTheme.typography.bodyMedium,
                            color = AppColors.TextSecondary
                        )
                    }
                }
                else -> {
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 12.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        uiState.sections.forEachIndexed { index, section ->
                            val accent = categoryAccents[index % categoryAccents.size]
                            // Section Header
                            item(key = "header_${section.category}") {
                                CategoryHeader(
                                    category = section.category,
                                    noteCount = section.notes.size,
                                    accentColor = accent,
                                    onDeleteCategoryClick = { categoryToDelete = section }
                                )
                            }
                            // Notes in section
                            items(
                                items = section.notes,
                                key = { it.id }
                            ) { note ->
                                NoteItem(
                                    note = note,
                                    accentColor = accent,
                                    onItemClick = { navigator?.navigateToViewNote(note.id) },
                                    onEditClick = { navigator?.navigateToUpdateNote(note.id) },
                                    onDeleteClick = { noteToDelete = note }
                                )
                            }
                            // Spacer between sections
                            item(key = "spacer_${section.category}") {
                                Spacer(modifier = Modifier.height(4.dp))
                            }
                        }
                    }
                }
            }
        }
    }

    // Delete note confirmation dialog
    noteToDelete?.let { note ->
        AlertDialog(
            onDismissRequest = { noteToDelete = null },
            containerColor = AppColors.Surface,
            title = {
                Text(
                    text = "Delete Note?",
                    fontWeight = FontWeight.Bold,
                    color = AppColors.TextPrimary
                )
            },
            text = {
                Text(
                    text = "This note will be permanently deleted. Are you sure?",
                    color = AppColors.TextSecondary
                )
            },
            confirmButton = {
                TextButton(onClick = {
                    viewModel.deleteNote(note.id)
                    noteToDelete = null
                }) {
                    Text("Delete", color = AppColors.Error, fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = {
                TextButton(onClick = { noteToDelete = null }) {
                    Text("Cancel", color = AppColors.Primary)
                }
            }
        )
    }

    // Delete category confirmation dialog
    categoryToDelete?.let { section ->
        AlertDialog(
            onDismissRequest = { categoryToDelete = null },
            containerColor = AppColors.Surface,
            title = {
                Text(
                    text = "Delete \"${section.category}\"?",
                    fontWeight = FontWeight.Bold,
                    color = AppColors.TextPrimary
                )
            },
            text = {
                Text(
                    text = "This will permanently delete the category and all ${section.notes.size} note(s) inside it. This cannot be undone.",
                    color = AppColors.TextSecondary
                )
            },
            confirmButton = {
                TextButton(onClick = {
                    viewModel.deleteCategory(section.category)
                    categoryToDelete = null
                }) {
                    Text("Delete All", color = AppColors.Error, fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = {
                TextButton(onClick = { categoryToDelete = null }) {
                    Text("Cancel", color = AppColors.Primary)
                }
            }
        )
    }
}

@Composable
private fun CategoryHeader(
    category: String,
    noteCount: Int,
    accentColor: Color,
    onDeleteCategoryClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 10.dp, horizontal = 4.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Left: colored bar + category name
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.weight(1f)
        ) {
            Box(
                modifier = Modifier
                    .width(4.dp)
                    .height(20.dp)
                    .clip(RoundedCornerShape(2.dp))
                    .background(accentColor)
            )
            Spacer(modifier = Modifier.width(10.dp))
            Text(
                text = category,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                color = AppColors.TextPrimary
            )
        }
        // Right: count badge + delete button
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(12.dp))
                    .background(accentColor.copy(alpha = 0.15f))
                    .padding(horizontal = 10.dp, vertical = 3.dp)
            ) {
                Text(
                    text = "$noteCount",
                    style = MaterialTheme.typography.bodySmall,
                    fontWeight = FontWeight.Bold,
                    color = accentColor
                )
            }
            IconButton(
                onClick = onDeleteCategoryClick,
                modifier = Modifier.size(32.dp)
            ) {
                DeleteIcon(
                    contentDescription = "Delete category",
                    modifier = Modifier.size(16.dp)
                )
            }
        }
    }
}

@Composable
private fun NoteItem(
    note: Note,
    accentColor: Color,
    onItemClick: () -> Unit,
    onEditClick: () -> Unit,
    onDeleteClick: () -> Unit
) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onItemClick() },
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = AppColors.Surface)
    ) {
        Row(modifier = Modifier.fillMaxWidth()) {
            // Colored left border
            Box(
                modifier = Modifier
                    .width(4.dp)
                    .fillMaxHeight()
                    .background(accentColor, RoundedCornerShape(topStart = 12.dp, bottomStart = 12.dp))
            )
            Column(
                modifier = Modifier
                    .weight(1f)
                    .padding(horizontal = 14.dp, vertical = 12.dp)
            ) {
                Text(
                    text = note.content,
                    style = MaterialTheme.typography.bodyLarge,
                    color = AppColors.TextPrimary,
                    maxLines = 3,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.fillMaxWidth()
                )
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = formatDate(note.createdAt),
                    style = MaterialTheme.typography.bodySmall,
                    color = AppColors.TextHint,
                    modifier = Modifier.align(Alignment.End)
                )
            }
            // Action icons
            Column(
                modifier = Modifier.padding(end = 4.dp, top = 4.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                IconButton(onClick = onEditClick) {
                    EditIcon(
                        contentDescription = "Edit",
                        modifier = Modifier.size(22.dp)
                    )
                }
                IconButton(onClick = onDeleteClick) {
                    DeleteIcon(
                        contentDescription = "Delete",
                        modifier = Modifier.size(22.dp)
                    )
                }
            }
        }
    }
}


